import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { Reveal, MaskLine } from "@/components/Reveal";
import { useApp } from "@/context/AppContext";
import { fetchProducts } from "@/api";
import Seo, { SITE_URL } from "@/components/Seo";
import { ProductGridSkeleton, ProductsError } from "@/components/ProductsState";

export default function Catalogue() {
  const { t } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchProducts()
      .then(setProducts)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  useEffect(() => setTerm(q), [q]);

  const setCategory = (key) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("category");
    else next.set("category", key);
    setParams(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    const val = term.trim();
    if (val) next.set("q", val);
    else next.delete("q");
    setParams(next);
  };

  const clearSearch = () => {
    const next = new URLSearchParams(params);
    next.delete("q");
    setParams(next);
    setTerm("");
  };

  const filters = [
    { key: "all", label: t.shop.filters.all },
    { key: "clothing", label: t.shop.filters.clothing },
    { key: "bags", label: t.shop.filters.bags },
    { key: "shoes", label: t.shop.filters.shoes },
    { key: "jewellery", label: t.shop.filters.jewellery },
  ];

  const byCategory = category === "all" ? products : products.filter((p) => p.category === category);
  const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const visible = q
    ? byCategory.filter((p) =>
        norm(`${p.name} ${p.brand} ${p.subtitle_en} ${p.subtitle_ru}`).includes(norm(q))
      )
    : byCategory;

  const catSeo = (t.seo.categories && t.seo.categories[category]) || t.seo.catalogue;
  const canonicalPath = category === "all" ? "/catalogue" : `/catalogue?category=${category}`;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "BuyHeda", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: t.nav.catalogue, item: SITE_URL + "/catalogue" },
      ...(category !== "all"
        ? [{ "@type": "ListItem", position: 3, name: (t.shop.filters[category] || category), item: SITE_URL + canonicalPath }]
        : []),
    ],
  };

  return (
    <div className="pt-28 md:pt-40 pb-24" data-testid="catalogue-page">
      <Seo title={catSeo.title} description={catSeo.description} canonicalPath={canonicalPath} jsonLd={breadcrumbLd} />
      <header className="px-5 md:px-10 mb-5 md:mb-14">
        <Reveal><p className="text-xs uppercase tracking-[0.28em] text-muted mb-3 md:mb-6">{t.hero.eyebrow}</p></Reveal>
        <h1 className="font-display font-light text-[20vw] lg:text-[12rem] leading-[0.8] tracking-tighter">
          <MaskLine delay={0.1}>{t.nav.catalogue}</MaskLine>
        </h1>
      </header>

      {/* Search */}
      <div className="px-5 md:px-10 mb-6 md:mb-8">
        <form onSubmit={onSearch} className="flex items-center border border-ink/70 focus-within:border-ink transition-colors max-w-xl" data-testid="catalogue-search-form">
          <Search className="h-5 w-5 ml-4 text-ink/50" strokeWidth={1.4} />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t.home.searchPlaceholder}
            data-testid="catalogue-search-input"
            className="flex-1 bg-transparent px-4 py-3 text-base font-light focus:outline-none placeholder:text-ink/40"
          />
          {q && (
            <button type="button" onClick={clearSearch} data-testid="catalogue-search-clear" className="px-3 text-ink/50 hover:text-ink" aria-label="Clear search">
              <X className="h-4 w-4" />
            </button>
          )}
          <button type="submit" className="bg-ink text-paper px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink" data-testid="catalogue-search-submit">
            {t.home.search}
          </button>
        </form>
      </div>

      {/* Filters */}
      <div className="px-5 md:px-10 mb-6 md:mb-12 sticky top-16 md:top-20 z-30 bg-paper/85 backdrop-blur-md py-4 border-y border-line" data-testid="catalogue-filters">
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em]">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setCategory(f.key)}
              data-testid={`filter-${f.key}`}
              className={`transition-colors duration-300 link-underline ${category === f.key ? "text-ink" : "text-muted hover:text-ink"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!loading && !error && (
        <motion.div layout className="px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6 md:gap-y-16">
          {visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </motion.div>
      )}

      {loading && <div className="px-5 md:px-10"><ProductGridSkeleton count={8} /></div>}

      {error && <div className="px-5 md:px-10"><ProductsError onRetry={load} /></div>}

      {!loading && !error && visible.length === 0 && products.length > 0 && (
        <p className="px-5 md:px-10 mt-6 text-ink/50 font-light" data-testid="catalogue-empty">{t.shop.empty}</p>
      )}
    </div>
  );
}
